/**
 * Responsible-contact capture step (OCP-524).
 *
 * The orboto Terms oblige the account holder to name a responsible contact
 * person - somebody authorized to make and receive declarations on their
 * behalf - as part of the conclusion of the contract. The API enforces that
 * (checkout, free-plan activation and code redemption all fail closed with
 * `responsible_contact_missing`); this module is the matching UX so a customer
 * never meets that wall as a bare error.
 *
 * `useResponsibleContactGate()` wraps any buy action:
 *
 *   const contactGate = useResponsibleContactGate();
 *   ...
 *   await contactGate.ensure(async () => { ...start checkout... });
 *   ...
 *   {contactGate.element}
 *
 * `ensure` checks whether the account already named a contact. If it did, the
 * action runs straight away and the customer sees nothing. If it did not, the
 * modal opens, and the action runs only after the contact is saved.
 *
 * One component for every entry point so the wording of the authorization
 * confirmation - the thing we rely on later - can never drift between them.
 * The wording itself is served BY THE API next to the checkbox, so what the
 * customer reads and what we persist as evidence come from the same constant.
 *
 * OCP-526 split the file in two: `ResponsibleContactForm` is the bare form and
 * `ResponsibleContactModal` is the dialog shell around it. The three CHECKOUT
 * entry points (Products, Billing top-up, Mail connections) no longer use the
 * standalone gate - `OrderReviewStep` hosts the same form as the first step of
 * the order dialog, so naming the contact and placing the order read as one
 * flow instead of two stacked dialogs. `useResponsibleContactGate` remains for
 * the entry point that has no order summary to show: code redemption.
 *
 * OCP-895: the signup form names the contact for every new account, with the
 * same fields (`ResponsibleContactFields`) and the same API-served wording
 * (from the public `GET /v1/portal/auth/signup/declarations`). The gate, the
 * banner and the order-dialog step stay for accounts created before that.
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api, ApiError } from '../lib/api';

export interface ResponsibleContact {
  name: string;
  email: string;
  phone: string | null;
  position: string | null;
  set_at: string;
  set_by: string;
  source: 'portal' | 'operator';
}

export interface ResponsibleContactChange {
  id: string;
  changed_at: string;
  changed_by: string;
  source: 'portal' | 'operator';
  previous_name: string | null;
  previous_email: string | null;
  previous_phone: string | null;
  previous_position: string | null;
  new_name: string;
  new_email: string;
  new_phone: string | null;
  new_position: string | null;
  confirmation_text: string | null;
  notice_mail_id: string | null;
  notice_recipients: Array<{
    role: 'new_contact' | 'previous_contact' | 'account';
    email: string;
    message_id: string | null;
    error: string | null;
  }> | null;
  notice_mail_error: string | null;
  notice_reference: string | null;
}

export interface ResponsibleContactResponse {
  contact: ResponsibleContact | null;
  history: ResponsibleContactChange[];
  role: 'owner' | 'admin' | 'viewer' | 'billing-only';
  confirmation_text: string;
  /** OCP-846 - the declaration per portal language; the form shows (and the server stores) the one of the UI language. */
  confirmation_texts?: { de: string; en: string };
}

export const RESPONSIBLE_CONTACT_PATH = '/v1/portal/settings/responsible-contact';

/** Roles allowed to name or change the contact (mirrors the server gate). */
export function canManageResponsibleContact(
  role: ResponsibleContactResponse['role'] | null,
): boolean {
  return role === 'owner' || role === 'admin';
}

const inputCls =
  'mt-1 w-full rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1.5 text-sm disabled:opacity-60';

export interface ResponsibleContactDraft {
  name: string;
  email: string;
  phone: string;
  position: string;
  confirmed: boolean;
}

type Draft = ResponsibleContactDraft;

export const EMPTY_RESPONSIBLE_CONTACT_DRAFT: Draft = {
  name: '',
  email: '',
  phone: '',
  position: '',
  confirmed: false,
};

export interface ResponsibleContactGate {
  /**
   * Run `action` once the account has a named responsible contact. Opens the
   * capture modal first when it does not. Returns after the action resolves, or
   * immediately after opening the modal (the action then runs on save).
   */
  ensure: (action: () => Promise<void> | void) => Promise<void>;
  /** Render this somewhere in the page - it is the modal. */
  element: React.ReactNode;
}

export function useResponsibleContactGate(): ResponsibleContactGate {
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState<(() => Promise<void> | void) | null>(null);

  const ensure = useCallback(async (action: () => Promise<void> | void) => {
    try {
      const r = await api<ResponsibleContactResponse>(RESPONSIBLE_CONTACT_PATH);
      if (r.contact) {
        await action();
        return;
      }
    } catch {
      // The probe is an affordance, not the gate. If it fails (offline, 5xx)
      // let the action proceed - the server-side check is what actually
      // protects the contract conclusion, and it returns a message we render.
      await action();
      return;
    }
    // Store the action behind a setter callback so React does not invoke it
    // as a state updater.
    setPending(() => action);
    setOpen(true);
  }, []);

  const close = useCallback(() => {
    setOpen(false);
    setPending(null);
  }, []);

  const onSaved = useCallback(async () => {
    const action = pending;
    setOpen(false);
    setPending(null);
    if (action) await action();
  }, [pending]);

  return {
    ensure,
    element: open ? (
      <ResponsibleContactModal
        mode="gate"
        onCancel={close}
        onSaved={() => void onSaved()}
      />
    ) : null,
  };
}

/**
 * Dialog shell around {@link ResponsibleContactForm}. Used by the standalone
 * gate and by the Settings page.
 */
export function ResponsibleContactModal({
  mode,
  initial,
  onCancel,
  onSaved,
}: {
  mode: 'gate' | 'change';
  initial?: ResponsibleContact | null;
  onCancel: () => void;
  onSaved: (r: ResponsibleContactResponse) => void;
}) {
  const { t } = useTranslation('responsible-contact');
  const heading = mode === 'gate' ? t('modal.headingGate') : t('modal.headingChange');

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={heading}
      className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 p-4"
      onClick={onCancel}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5 shadow-xl"
      >
        <h2 className="text-base font-semibold">{heading}</h2>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
          {mode === 'gate' ? t('modal.introGate') : t('modal.introChange')}
        </p>
        <ResponsibleContactForm
          initial={initial}
          submitLabel={mode === 'gate' ? t('modal.submitGate') : t('modal.submitChange')}
          onCancel={onCancel}
          onSaved={onSaved}
        />
      </div>
    </div>
  );
}

/**
 * The capture form itself - fields, the verbatim authorization confirmation
 * and the save action, WITHOUT a dialog shell.
 *
 * Extracted (OCP-526) so the order review step can host it as its first step
 * instead of stacking a second dialog on top of the order summary: naming the
 * contact and placing the order are one moment in the flow, and the customer
 * should see one dialog that walks through it.
 */
export function ResponsibleContactForm({
  initial,
  submitLabel,
  cancelLabel,
  onCancel,
  onSaved,
}: {
  initial?: ResponsibleContact | null;
  submitLabel: string;
  cancelLabel?: string;
  onCancel: () => void;
  onSaved: (r: ResponsibleContactResponse) => void;
}) {
  const { t, i18n } = useTranslation('responsible-contact');
  // OCP-846 - the language of the sentence next to the checkbox; sent back so
  // the evidence row stores exactly the sentence that was on screen (AGB V4 4.4).
  const language: 'de' | 'en' = i18n.language?.startsWith('de') ? 'de' : 'en';
  const [draft, setDraft] = useState<Draft>(
    initial
      ? {
          name: initial.name,
          email: initial.email,
          phone: initial.phone ?? '',
          position: initial.position ?? '',
          confirmed: false,
        }
      : EMPTY_RESPONSIBLE_CONTACT_DRAFT,
  );
  const [confirmationText, setConfirmationText] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Pull the authoritative confirmation wording so the text the customer
  // agrees to is the text we store as evidence.
  useEffect(() => {
    const ac = new AbortController();
    void (async () => {
      try {
        const r = await api<ResponsibleContactResponse>(RESPONSIBLE_CONTACT_PATH, {
          signal: ac.signal,
        });
        setConfirmationText(r.confirmation_texts?.[language] ?? r.confirmation_text);
      } catch {
        // Fall back to the label below; the server stores its own constant.
      }
    })();
    return () => ac.abort();
  }, [language]);

  const valid = isResponsibleContactDraftValid(draft);

  async function submit() {
    if (saving || !valid) return;
    setSaving(true);
    setError(null);
    try {
      const r = await api<ResponsibleContactResponse>(RESPONSIBLE_CONTACT_PATH, {
        method: 'PATCH',
        body: {
          name: draft.name.trim(),
          email: draft.email.trim(),
          phone: draft.phone.trim() || null,
          position: draft.position.trim() || null,
          confirmed: true,
          confirmation_language: language,
        },
      });
      onSaved(r);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setError(t('form.errorRole'));
      } else if (err instanceof ApiError && err.status === 401) {
        setError(t('form.errorSession'));
      } else if (err instanceof ApiError) {
        setError(t('form.errorSaveCode', { code: err.code ?? err.status }));
      } else {
        setError(t('form.errorSave'));
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      {error && (
        <div
          role="alert"
          className="mt-4 rounded border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-800 dark:text-red-300"
        >
          {error}
        </div>
      )}

      <ResponsibleContactFields
        draft={draft}
        onChange={setDraft}
        confirmationText={confirmationText}
        autoFocus
      />

      <div className="mt-5 flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="rounded border border-gray-300 dark:border-gray-700 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/60"
        >
          {cancelLabel ?? t('form.cancel')}
        </button>
        <button
          type="button"
          onClick={() => void submit()}
          disabled={!valid || saving}
          className="rounded bg-brand-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-600 disabled:bg-gray-300"
          data-testid="responsible-contact-save"
        >
          {saving ? t('form.saving') : submitLabel}
        </button>
      </div>
    </>
  );
}

/** Name, email, phone and position look complete, and the authorization is ticked. */
export function isResponsibleContactDraftValid(draft: ResponsibleContactDraft): boolean {
  const emailLooksValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(draft.email.trim());
  return draft.name.trim().length > 0 && emailLooksValid && draft.confirmed;
}

/**
 * The contact fields and the verbatim authorization confirmation, without a
 * save action. Shared by {@link ResponsibleContactForm} (settings, order dialog,
 * redeem gate) and the signup form (OCP-895), so the fields and the checkbox
 * cannot differ between "named at signup" and "named or changed later".
 * `confirmationText` is the API-served sentence; null falls back to the
 * catalogue copy (the server stores its own constant either way).
 */
export function ResponsibleContactFields({
  draft,
  onChange,
  confirmationText,
  autoFocus = false,
}: {
  draft: ResponsibleContactDraft;
  onChange: (next: ResponsibleContactDraft) => void;
  confirmationText: string | null;
  autoFocus?: boolean;
}) {
  const { t } = useTranslation('responsible-contact');
  return (
    <div className="mt-4 space-y-3">
      <label className="block text-sm">
        <span className="text-gray-700 dark:text-gray-300">{t('form.fullName')}</span>
        <input
          type="text"
          autoFocus={autoFocus}
          value={draft.name}
          onChange={(e) => onChange({ ...draft, name: e.target.value })}
          maxLength={200}
          placeholder={t('form.namePlaceholder')}
          className={inputCls}
          data-testid="responsible-contact-name"
        />
      </label>
      <label className="block text-sm">
        <span className="text-gray-700 dark:text-gray-300">{t('form.email')}</span>
        <input
          type="email"
          value={draft.email}
          onChange={(e) => onChange({ ...draft, email: e.target.value })}
          maxLength={254}
          placeholder={t('form.emailPlaceholder')}
          className={inputCls}
          data-testid="responsible-contact-email"
        />
        <span className="mt-1 block text-xs text-gray-500">
          {t('form.emailHelp')}
        </span>
      </label>
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block text-sm">
          <span className="text-gray-700 dark:text-gray-300">
            {t('form.phone')} <span className="text-gray-500">{t('form.optional')}</span>
          </span>
          <input
            type="tel"
            value={draft.phone}
            onChange={(e) => onChange({ ...draft, phone: e.target.value })}
            maxLength={64}
            className={inputCls}
            data-testid="responsible-contact-phone"
          />
        </label>
        <label className="block text-sm">
          <span className="text-gray-700 dark:text-gray-300">
            {t('form.position')} <span className="text-gray-500">{t('form.optional')}</span>
          </span>
          <input
            type="text"
            value={draft.position}
            onChange={(e) => onChange({ ...draft, position: e.target.value })}
            maxLength={200}
            className={inputCls}
            data-testid="responsible-contact-position"
          />
        </label>
      </div>

      <label className="flex items-start gap-2 rounded border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/40 p-3 text-sm">
        <input
          type="checkbox"
          checked={draft.confirmed}
          onChange={(e) => onChange({ ...draft, confirmed: e.target.checked })}
          className="mt-0.5"
          data-testid="responsible-contact-confirm"
        />
        <span className="text-gray-700 dark:text-gray-300">
          {confirmationText ?? t('form.confirmation')}
        </span>
      </label>
    </div>
  );
}
